import { useQuery } from '@apollo/client';
import {
  ALL_MODULES,
  ALL_SHARD_MODULES,
  AsyncPropSelector,
  getTranslatedText,
  NEW_MODULE_MAP,
  T1
} from '@nile/components';
import { GET_FIELD, AccountClient, SystrayAccountClient } from '@nile/graphql';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import {
  Popover,
  Button,
  ButtonGroup,
  TextStyle,
  Tippy
} from '@superops/ui-design-system';
import {
  DropdownIndicator,
  formatGroupLabel,
  formatOptionLabel,
  IndicatorSeparator
} from '@nile/components';
import { Edit } from '@styled-icons/material/Edit';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import Select from 'react-select';
import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  reactSelectErrorStyles,
  reactSelectInlineError
} from '@nile/banyan/src/Utils/style';
import { EditIcon } from '@nile/components/src/Icons';
import { getCellValue } from '@nile/reports/src/modules/EfficiencyReport/util';
import { GET_OPTIONS } from '@nile/banyan/src/Graphql/Mutations/ticket';
import { useIntl } from 'react-intl';

const CustomField = styled.div`
  min-width: 200px;
  max-width: ${(props) => (props?.fullWidth ? 'unset' : '250px')};
  flex-grow: 1;
  padding-right: ${(props) => !props?.fullWidth && '10px'};
  padding-left: ${(props) => !props?.fullWidth && '10px'};
  margin-top: 8px;
  margin-bottom: 8px;
  .Polaris-ButtonGroup {
    margin-top: 12px;
  }
`;

const Label = styled.div`
  text-align: left;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 10px;
`;

const Wrap = styled.div`
  display: flex;
  align-items: center;
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  padding-left: 8px;
  padding-right: 8px;
  max-width: 100%;
`;

const CField = styled.div`
  .editable-cell,
  .cell {
    flex-grow: 1;
    font-weight: bold;
  }
  &:hover {
    .edit-actions {
      visibility: visible;
    }
  }
`;

function AsyncSelectNew(props) {
  const {
    field,
    data: rawData,
    defaultValue,
    updateSelection,
    editable,
    value,
    inlineEdit,
    isMulti,
    disableSelection,
    isMandatory,
    systray = false
  } = props;
  const intl = useIntl();
  const [options, setOptions] = useState([]);
  const [optionLoading, setOptionLoading] = useState(false);

  const CLIENT = systray ? SystrayAccountClient : AccountClient;
  const { data, loading, error } = useQuery(GET_FIELD, {
    client: CLIENT,
    variables: {
      input: {
        columnName: field?.columnName || field?.name,
        entity:
          NEW_MODULE_MAP[props?.module] ||
          ALL_SHARD_MODULES[props?.module] ||
          ALL_SHARD_MODULES.ticket
      }
    },
    onCompleted({ getField }) {
      if (getField) {
        if (isEmpty(getField?.parentField)) {
          const options = getField?.options || getField?.fieldOptions;
          const customOptions = options?.map((el) => {
            return {
              name: el?.value,
              value: el?.value,
              label: el?.value,
              parentOption: el?.parentOption
            };
          });
          setOptions(customOptions);
        }
      }
    }
  });

  const updateSelectedField = (val) => {
    if (!val) {
      updateSelection(null);
    }
    updateSelection(val);
  };
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  });
  const [updatedValue, setUpdatedValue] = useState(value);

  useEffect(() => {
    if (!isEqual(updatedValue, value)) {
      setUpdatedValue(value);
    }
  }, [value]);

  const updateUpdatedValue = (val) => {
    if (!val) {
      setUpdatedValue(null);
    }
    setUpdatedValue(val);
  };

  const filterOptions = () => {
    if (data?.getField?.parentField) {
      setOptionLoading(true);
      const key =
        data?.getField?.parentField?.fieldCategory === 'CUSTOM'
          ? `customFields.${field?.parentField?.columnName || field?.nestedTo}`
          : field?.parentField?.columnName || field?.nestedTo;
      const parentValue = getCellValue(rawData, key);
      const prntValue =
        typeof parentValue === 'object'
          ? parentValue?.name || parentValue?.id
          : parentValue;
      CLIENT.query({
        query: GET_OPTIONS,
        variables: {
          input: {
            field: { id: data?.getField?.id, module: data?.getField?.module },
            parentOptionValue: { value: prntValue }
          }
        }
      })
        .then((res) => {
          const opts = res?.data?.getOptions?.map((el) => ({
            name: el?.value,
            value: el?.value,
            label: el?.value,
            parentOption: el?.parentOption
          }));
          setOptions(opts);
        })
        .finally(() => setOptionLoading(false));
    }
  };

  if (loading) return <div>loading...</div>;

  const displayVal = value
    ? isMulti
      ? value.map((el) => el.label).join(', ')
      : value.label
    : '';

  const activator = <EditIcon onClick={() => togglePopoverActive()} />;
  const getTag = () => {
    return (
      <CustomField className="cf-wrapper">
        <Label className="cf-label">
          {field.label || ''}{' '}
          {isMandatory && (
            <>
              <span className="mandatory-star">*</span>
            </>
          )}
        </Label>
        <div className="flex col" key={field.name}>
          <Select
            className="propSelector"
            key={defaultValue}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            formatOptionLabel={formatOptionLabel}
            defaultValue={updatedValue}
            isLoading={loading || optionLoading}
            components={{ IndicatorSeparator, DropdownIndicator }}
            onChange={updateUpdatedValue}
            options={options}
            placeholder="Choose value"
            menuPortalTarget={document.body}
            onMenuOpen={filterOptions}
            menuPlacement="auto"
            closeMenuOnScroll={(e) => {
              e.stopPropagation();
              e.preventDefault();
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            noOptionsMessage={() => {
              return loading ? 'Loading...' : 'No options';
            }}
            isMulti={isMulti}
            isDisabled={disableSelection}
            styles={
              props.error
                ? {
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    ...reactSelectErrorStyles
                  }
                : {
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    })
                  }
            }
          />
          {props.error &&
            props.showErrorMessage &&
            reactSelectInlineError(props.error)}
        </div>
        <div className="mt10">
          <ButtonGroup>
            <Button
              onClick={() => {
                togglePopoverActive();
                setUpdatedValue(value);
              }}
            >
              Cancel
            </Button>
            <Button
              primary
              onClick={() => {
                updateSelectedField(updatedValue);
                togglePopoverActive();
              }}
            >
              Save
            </Button>
          </ButtonGroup>
        </div>
      </CustomField>
    );
  };

  if (inlineEdit) {
    if (field?.isDependent && !editable) {
      return (
        <Tippy
          title={`${getTranslatedText(
            intl,
            'common.ticket.field.dependant.title',
            {
              name: field?.parent?.label
                ? getTranslatedText(intl, field?.parent?.label)
                : ''
            }
          )}`}
          duration={250}
          delay={100}
          placement="bottom-start"
          arrow={false}
          theme="dark"
          appendTo={document.body}
        >
          <CField className="disable-actions">
            <Wrap>
              <div className="ellipsis" title={displayVal}>
                {displayVal || (
                  <TextStyle variation="subdued">
                    {getTranslatedText(intl, 'app.noData.message')}
                  </TextStyle>
                )}
              </div>
            </Wrap>
          </CField>
        </Tippy>
      );
    }
    return !popoverActive ? (
      <CField>
        <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
          <div className="ellipsis" title={displayVal}>
            {displayVal || (
              <TextStyle variation="subdued">
                {getTranslatedText(intl, 'app.noData.message')}
              </TextStyle>
            )}
          </div>
          {editable && <div className="edit-actions">{activator}</div>}
        </Wrap>
      </CField>
    ) : (
      <CField>
        <Wrap className="editable-cell">
          <div className="ellipsis" title={displayVal}>
            {displayVal || (
              <TextStyle variation="subdued">
                {getTranslatedText(intl, 'app.noData.message')}
              </TextStyle>
            )}
          </div>
          <Popover
            activator={activator}
            active={popoverActive}
            onClose={() => {
              // togglePopoverActive()
            }}
          >
            {getTag()}
          </Popover>
        </Wrap>
      </CField>
    );
  }
  return (
    <CustomField fullWidth={props?.fullWidth} className="cf-wrapper">
      <Label className="cf-label">
        {field.label || ''}
        {isMandatory && (
          <>
            <span className="mandatory-star">*</span>
          </>
        )}
      </Label>
      <div className="flex col" key={field.name}>
        <Select
          className="propSelector"
          key={defaultValue}
          classNamePrefix="value_selector"
          formatGroupLabel={formatGroupLabel}
          formatOptionLabel={formatOptionLabel}
          defaultValue={value}
          components={{ IndicatorSeparator, DropdownIndicator }}
          onChange={updateSelectedField}
          options={options}
          isLoading={loading || optionLoading}
          onMenuOpen={filterOptions}
          placeholder="Choose value"
          menuPortalTarget={document.body}
          menuPlacement="auto"
          closeMenuOnScroll={(e) => {
            e.stopPropagation();
            e.preventDefault();
            return (
              e.target &&
              e.target.classList &&
              !(e.target.classList.value.indexOf('menu-list') > -1)
            );
          }}
          styles={
            props.error
              ? {
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999
                  }),
                  ...reactSelectErrorStyles
                }
              : {
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }
          }
          noOptionsMessage={() => {
            return loading ? 'Loading...' : 'No options';
          }}
          isMulti={isMulti}
          isDisabled={disableSelection}
        />
        {props.error &&
          props.showErrorMessage &&
          reactSelectInlineError(props.error)}
      </div>
    </CustomField>
  );
}

export default AsyncSelectNew;
